import React, { useState, useEffect } from 'react';
import { DashboardModal } from 'containers/Dashboard/components/DashboardModal';
import { Button, Alert, Table } from 'reactstrap';
import Dropzone from 'react-dropzone';
import __ from 'utils/i18n';
import Loader from 'components/Loader';
import { RecurringPaymentsCsvValidator } from 'utils/recurringPaymentsCsvValidator';
import { confirmBulkUpdate } from '../../_shared/resources';
import { fetchResourceRP } from '../../../_shared/fetchResourceRP';
import { backendUrl, tenantId } from 'config/envConfig';
import { useSelector } from 'react-redux';
import { notification } from 'utils/notifications';

export const UpdateAmountsModal = ({ 
  showModal = false, 
  hideModal, 
  plan, 
  onSuccess,
  onStartProcessing 
}) => {
  const { token } = useSelector((state) => state.api);
  const [file, setFile] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [, setValidationProgress] = useState(0);
  const [validationResult, setValidationResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [currentStep, setCurrentStep] = useState('upload'); // 'upload', 'validated', 'uploading', 'confirm', 'success', 'error', 'simple_error'
  const [simpleError, setSimpleError] = useState(null); // Para errores de formato/archivo vacío
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  const validator = new RecurringPaymentsCsvValidator();

  // Reset state when modal closes
  useEffect(() => {
    if (!showModal) {
      setFile(null);
      setValidationResult(null);
      setUploadResult(null);
      setCurrentStep('upload');
      setIsValidating(false);
      setIsUploading(false);
      setSimpleError(null);
    }
  }, [showModal]);

  const handleDownloadTemplate = async () => {
    if (!token) {
      notification.error('Error: No se encontró el token de autenticación. Por favor, recarga la página.');
      return;
    }

    setIsDownloadingTemplate(true);
    
    try {
      // Llamar al endpoint del backend
      const response = await fetch(
        `${backendUrl}/api/v1/recurring-payments/plans/${plan.planId}/bulk-update-template`,
        {
          method: 'GET',
          headers: {
            'x-client-id': tenantId,
            'x-Authentication-Token': token,
            'x-request-download': 'stream',
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          notification.error('Este plan no tiene suscriptores activos. Para actualizar montos, el plan debe tener al menos un suscriptor activo.');
          setIsDownloadingTemplate(false);
          return;
        }
        throw new Error('Error al descargar la plantilla');
      }

      // Obtener el blob del CSV
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Obtener el nombre del archivo del header Content-Disposition o usar uno por defecto
      const contentDisposition = response.headers.get('Content-Disposition');
      const sanitizedPlanName = (plan.name || 'Plan').replace(/[^a-zA-Z0-9\s]/g, '').trim();
      let filename = `plantilla_${plan.planId}_${sanitizedPlanName}.csv`;
      if (contentDisposition) {
        // Use ([^"]+) so the closing quote is not captured (".+" leaves a trailing ",
        // which browsers sanitize to "_" → e.g. "file.csv_").
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1].trim();
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      URL.revokeObjectURL(url);
      
      notification.success('Plantilla descargada exitosamente');

    } catch (error) {
      notification.error('Error al generar la plantilla. Por favor intente nuevamente.');
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleValidateFile = async (selectedFile) => {
    setIsValidating(true);
    setValidationProgress(0);
    setValidationResult(null);
    setCurrentStep('upload');

    try {
      const result = await validator.validateFile(
        selectedFile,
        (progress) => setValidationProgress(progress)
      );

      setIsValidating(false);
      const formattedResult = validator.formatErrorReport(result);

      // Validación 3: Verificar si hay error de header (columnas incorrectas)
      const hasHeaderError = result.errors && result.errors.length > 0 && 
                             result.errors.some(err => err.field === 'header');
      
      if (hasHeaderError) {
        const headerError = result.errors.find(err => err.field === 'header');
        setSimpleError({
          title: 'El archivo tiene un formato inválido',
          message: headerError.error
        });
        setCurrentStep('simple_error');
        return;
      }

      // REMOVED: Validación especial de errores de ID/HASH
      // Ahora los errores de ID y HASH se muestran en la tabla de errores normal
      
      // Validación 4: Verificar cantidad de filas vs suscriptores del plan
      const totalSubscribers = plan?.totalSubscriptions || 0;
      const validRowsCount = result.validRows || 0;
      const totalRows = result.totalRows || 0;
      const hasErrors = result.errors && result.errors.length > 0;
      
      // Si no hay filas válidas PERO hay errores de validación, mostrar tabla detallada
      if (validRowsCount === 0 && hasErrors) {
        setValidationResult(formattedResult);
        setCurrentStep('error');
        return;
      }
      
      // Si no hay filas válidas y NO hay errores, el archivo está vacío de datos
      if (validRowsCount === 0 && !hasErrors) {
        setSimpleError({
          title: 'El archivo no tiene datos válidos',
          message: 'El archivo que cargaste no contiene información válida.\nPor favor, completalo con datos correctos y volvé a intentarlo.'
        });
        setCurrentStep('simple_error');
        return;
      }

      if (totalRows !== totalSubscribers) {
        setSimpleError({
          title: 'La cantidad de filas no coincide',
          message: `El archivo tiene ${totalRows} ${totalRows === 1 ? 'fila' : 'filas'} pero el plan tiene ${totalSubscribers} ${totalSubscribers === 1 ? 'suscriptor' : 'suscriptores'}.\nEl archivo debe tener la misma cantidad de filas que de suscriptores.`
        });
        setCurrentStep('simple_error');
        return;
      }

      setValidationResult(formattedResult);

      if (formattedResult.isValid) {
        setCurrentStep('validated');
      } else {
        setCurrentStep('error');
      }
    } catch (error) {
      setIsValidating(false);
      setValidationResult({
        isValid: false,
        summary: `Error al validar archivo: ${error.message}`
      });
      setCurrentStep('error');
    }
  };

  const handleUpload = async () => {
    if (!file || !validationResult?.isValid) {
      return;
    }

    setIsUploading(true);
    setCurrentStep('uploading');

    try {
      // NUEVA ESTRATEGIA: Primero obtener el topic, luego iniciar suscripción SSE, luego llamar al API
      // Generar el mismo topic que generará el backend (formato: urn:{tenantId}:bulk_update_{planId})
      const predictedTopic = `urn:${tenantId}:bulk_update_${plan.planId}`;
      
      // Cerrar este modal y abrir el modal de progreso ANTES de hacer el upload
      hideModal();
      
      if (onStartProcessing) {
        const processingData = {
          totalRows: validationResult.validRows || 0,
          streamHubTopic: predictedTopic,
          plan: plan,
          pendingUpload: true, // Flag para indicar que aún no se hizo el upload
          file: file, // Pasar el archivo para que el modal de progreso lo suba
        };
        
        onStartProcessing(processingData);
      }
      
      setIsUploading(false);
      
    } catch (error) {
      setIsUploading(false);
      
      // Extraer mensaje de error de diferentes formatos posibles
      let errorMessage = 'Error al procesar el archivo';
      if (error?.response?.data?.response?.message) {
        errorMessage = error.response.data.response.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.errors?.[0]?.detail) {
        errorMessage = error.response.data.errors[0].detail;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Cerrar el modal y mostrar el error en el modal de progreso
      hideModal();
      if (onStartProcessing) {
        onStartProcessing({
          error: error,
          errorMessage: errorMessage,
          totalRows: 0,
          plan: plan
        });
      } else {
        setUploadResult({
          errors: [{ message: errorMessage }]
        });
        setCurrentStep('error');
      }
    }
  };

  const handleConfirm = async () => {
    if (!uploadResult?.importFileName) return;

    setIsUploading(true);

    try {
      const response = await fetchResourceRP(() => confirmBulkUpdate(uploadResult.importFileName, token, plan.planId));

      setIsUploading(false);

      if (response?.data?.success === 'OK') {
        setCurrentStep('success');
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
            hideModal();
          }, 2000);
        }
      } else {
        setCurrentStep('error');
        const errorMsg = 'Error al confirmar la actualización';
        setUploadResult({
          errors: [{ message: errorMsg }]
        });
        notification.error(errorMsg);
      }
    } catch (error) {
      setIsUploading(false);
      setCurrentStep('error');
      setUploadResult({
        errors: [{ message: error.message || 'Error al confirmar' }]
      });
    }
  };

  const onDrop = (acceptedFiles, rejectedFiles) => {
    // Validación 1: Verificar que sea un archivo CSV
    if (rejectedFiles && rejectedFiles.length > 0) {  
      const msg = 'El archivo que cargaste debe ser en formato CSV.\nPor favor, revisalo y volvé a intentarlo.';
      notification.error(msg);
      setSimpleError({
        title: 'El archivo tiene un formato inválido',
        message: msg
      });
      setCurrentStep('simple_error');
      return;
    }

    if (acceptedFiles && acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      
      // Validación 1 adicional: Verificar extensión
      if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
        const msg = 'El archivo que cargaste debe ser en formato CSV.\nPor favor, revisalo y volvé a intentarlo.';
        notification.error(msg);
        setSimpleError({
          title: 'El archivo tiene un formato inválido',
          message: msg
        });
        setCurrentStep('simple_error');
        return;
      }

      // Validación 2: Verificar que el archivo no esté vacío
      if (selectedFile.size === 0) {
        const msg = 'El archivo que cargaste no tiene datos ingresados.\nPor favor, completalo y volvé a intentarlo.';
        notification.error(msg);
        setSimpleError({
          title: 'El archivo está vacío',
          message: msg
        });
        setCurrentStep('simple_error');
        return;
      }

      // Validación 3: Verificar que el archivo corresponda al plan actual
      const fileName = selectedFile.name;
      const fileNamePattern = /^plantilla_([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})_/i;
      const match = fileName.match(fileNamePattern);

      if (!match) {
        const msg = 'El nombre del archivo no tiene el formato esperado.\nPor favor, descargá nuevamente la plantilla desde este plan.';
        notification.error(msg);
        setSimpleError({
          title: 'El archivo tiene un formato inválido',
          message: msg
        });
        setCurrentStep('simple_error');
        return;
      }

      const fileplanId = match[1].toLowerCase();
      const currentPlanId = plan.planId.toLowerCase();

      if (fileplanId !== currentPlanId) {
        setSimpleError({
          title: 'El archivo no corresponde a este plan',
          message: 'Estás intentando cargar un archivo de otro plan.\nPor favor, descargá la plantilla correcta desde este plan o seleccioná el plan correspondiente al archivo.'
        });
        setCurrentStep('simple_error');
        return;
      }

      setFile(selectedFile);
      handleValidateFile(selectedFile);
    }
  };

  const handleReset = () => {
    setFile(null);
    setValidationResult(null);
    setUploadResult(null);
    setCurrentStep('upload');
    setSimpleError(null);
  };

  if (!plan) return null;

  const renderContent = () => {
    // Estado: Validando
    if (isValidating) {
      return (
        <div className="text-center" style={{ 
          minHeight: '400px', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center',
          backgroundColor: '#fff'
        }}>
          <div style={{ position: 'relative', height: '80px', marginBottom: '20px' }}>
            <Loader isLoading={true} absolute={false} />
          </div>
          <p className="mt-3 mb-2">Estamos analizando el archivo</p>
          <p className="text-muted">Por favor, aguardá unos segundos...</p>
        </div>
      );
    }

    // Estado: Error simple (formato/vacío) - Pantalla blanca completa
    if (currentStep === 'simple_error' && simpleError) {
      return (
        <div className="text-center" style={{ 
          minHeight: '400px', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center',
          backgroundColor: '#fff'
        }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            borderRadius: '50%', 
            backgroundColor: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '30px'
          }}>
            <i className="mdi mdi-alert-circle mdi-48px" style={{ color: '#dc3545' }}></i>
          </div>
          <h4 className="mb-3" style={{ fontWeight: 'bold', color: '#000' }}>{simpleError.title}</h4>
          <p style={{ whiteSpace: 'pre-line', maxWidth: '500px', color: '#000' }}>
            {simpleError.message}
          </p>
          <Button 
            color="light" 
            outline 
            onClick={handleReset} 
            className="mt-4"
            style={{ minWidth: '150px' }}
          >
            Reintentar
          </Button>
        </div>
      );
    }

    // Estado: Error de validación de contenido (con tabla de errores)
    if (currentStep === 'error' && validationResult && !validationResult.isValid) {
      const totalRows = validationResult.totalRows || 0;
      const correctRows = validationResult.validRows || 0;
      const errorRows = validationResult.totalErrorRows || 0;
      
      return (
        <div className="py-4">
          {/* Nombre del archivo */}
          <div className="mb-3">
            <small className="text-muted d-block mb-1">Archivo</small>
            <p className="mb-0 text-dark">{file?.name}</p>
          </div>
          
          {/* Línea divisoria */}
          <hr className="my-3" />
          
          {/* Estadísticas */}
          <div className="row mb-4">
            <div className="col">
              <div className="rounded p-3 h-100" style={{ border: '1px solid #dee2e6' }}>
                <small className="text-muted d-block mb-2">Total de registros</small>
                <h3 className="mb-0 font-weight-bold text-left" style={{ color: '#000' }}>{totalRows}</h3>
              </div>
            </div>
            <div className="col">
              <div className="rounded p-3 h-100" style={{ border: '1px solid #dee2e6' }}>
                <small className="text-muted d-block mb-2">Correctos</small>
                <h3 className="mb-0 font-weight-bold text-success text-left">{correctRows}</h3>
              </div>
            </div>
            <div className="col">
              <div className="rounded p-3 h-100" style={{ border: '1px solid #dee2e6' }}>
                <small className="text-muted d-block mb-2">Con error</small>
                <h3 className="mb-0 font-weight-bold text-danger text-left">{errorRows}</h3>
              </div>
            </div>
          </div>
          
          {/* Badge de registros con error */}
          <div className="d-flex align-items-center mb-3">
            <span className="badge badge-danger">
              <i className="mdi mdi-alert-circle mr-1"></i>
              Registros con error ({errorRows})
            </span>
          </div>
          
          {/* Tabla de errores */}
          {validationResult.errorsByRow && (
            <div className="mb-4">
              <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <Table className="table-hover mb-0">
                  <thead>
                    <tr>
                      <th className="text-muted" style={{ width: '100px', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Línea</th>
                      <th className="text-muted" style={{ width: '180px', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Campo</th>
                      <th className="text-muted" style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(validationResult.errorsByRow).map(([rowNum, errors]) =>
                      errors.map((error, idx) => (
                        <tr key={`${rowNum}-${idx}`}>
                          <td className="align-middle">
                            <span className="text-muted">{rowNum}</span>
                          </td>
                          <td className="align-middle">
                            <code className="text-danger font-weight-bold">{error.field}</code>
                          </td>
                          <td className="align-middle">{error.error}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              {validationResult.hasMoreErrors && (
                <small className="text-muted d-block mt-3">
                  <i className="mdi mdi-information-outline mr-1"></i>
                  Mostrando primeros 50 errores. Total: {validationResult.totalErrorRows} filas con errores.
                </small>
              )}
            </div>
          )}
          
          {/* Mensaje de advertencia */}
          <Alert color="warning" className="mb-4">
            <p className="mb-2">
              Para continuar, corregí los errores que presenta el archivo y volvé a cargarlo para actualizar los montos del plan.
            </p>
            <p className="mb-0">
              <strong>Recordá no modificar los datos que ya están completos.</strong>
            </p>
          </Alert>
          
          {/* Botón */}
          <div className="d-flex justify-content-end">
            <Button 
              color="light" 
              outline 
              onClick={handleReset}
            >
              Volver a cargar
            </Button>
          </div>
        </div>
      );
    }

    // Estado: Archivo validado correctamente
    if (currentStep === 'validated' && validationResult?.isValid) {
      const totalRows = validationResult.validRows || 0;
      const correctRows = validationResult.validRows || 0;
      const errorRows = 0; 
      
      return (
        <div className="py-4">
          {/* Mensaje de éxito */}
          <Alert color="success" className="d-flex align-items-center mb-4">
            <i className="mdi mdi-check-circle mr-2" style={{ fontSize: '20px' }}></i>
            <span className="font-weight-bold">Archivo validado con éxito</span>
          </Alert>
          
          {/* Nombre del archivo */}
          <div className="mb-3">
            <small className="text-muted d-block mb-1">Archivo</small>
            <p className="mb-0 text-dark">{file?.name}</p>
          </div>
          
          {/* Línea divisoria */}
          <hr className="my-3" />
          
          {/* Estadísticas */}
          <div className="row mb-4">
            <div className="col">
              <div className="rounded p-3 h-100" style={{ border: '1px solid #dee2e6' }}>
                <small className="text-muted d-block mb-2">Total de registros</small>
                <h3 className="mb-0 font-weight-bold text-left" style={{ color: '#000' }}>{totalRows}</h3>
              </div>
            </div>
            <div className="col">
              <div className="rounded p-3 h-100" style={{ border: '1px solid #dee2e6' }}>
                <small className="text-muted d-block mb-2">Correctos</small>
                <h3 className="mb-0 font-weight-bold text-success text-left">{correctRows}</h3>
              </div>
            </div>
            <div className="col">
              <div className="rounded p-3 h-100" style={{ border: '1px solid #dee2e6' }}>
                <small className="text-muted d-block mb-2">Con error</small>
                <h3 className="mb-0 font-weight-bold text-danger text-left">{errorRows}</h3>
              </div>
            </div>
          </div>
          
          {/* Botones */}
          <div className="d-flex justify-content-end">
            <Button 
              color="light" 
              outline 
              onClick={handleReset}
              className="mr-2"
            >
              Volver
            </Button>
            <Button 
              color="primary" 
              onClick={handleUpload}
            >
              Actualizar montos
            </Button>
          </div>
        </div>
      );
    }

    // Estado: Subiendo/Procesando
    if (currentStep === 'uploading' || isUploading) {
      return (
        <div className="text-center" style={{ 
          minHeight: '400px', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center',
          backgroundColor: '#fff'
        }}>
          <div style={{ position: 'relative', height: '80px', marginBottom: '20px' }}>
            <Loader isLoading={true} absolute={false} />
          </div>
          <p className="mt-3 mb-2">Estamos procesando la actualización</p>
          <p className="text-muted">Por favor, aguardá unos segundos...</p>
        </div>
      );
    }

    // Estado: Confirmar actualización
    if (currentStep === 'confirm' && uploadResult) {
      return (
        <div>
          <Alert color="info">
            <h5 className="alert-heading">
              <i className="mdi mdi-information mr-2"></i>
              Confirmación requerida
            </h5>
            <hr />
            <p>
              Se actualizarán <strong>{uploadResult.updateQuantity || validationResult?.validRows}</strong> suscripciones.
            </p>
            <p className="mb-0">
              ¿Desea continuar con esta operación? Este cambio puede tardar unos momentos en completarse.
            </p>
          </Alert>
          <div className="mt-3 text-right">
            <Button color="success" onClick={handleConfirm}>
              <i className="mdi mdi-check mr-1"></i>
              Confirmar actualización
            </Button>
          </div>
        </div>
      );
    }

    // Estado: Éxito
    if (currentStep === 'success') {
      return (
        <Alert color="success" className="text-center py-5">
          <i className="mdi mdi-check-circle mdi-48px d-block mb-3"></i>
          <h4>¡Actualización completada!</h4>
          <p className="mb-0">Los montos se han actualizado correctamente.</p>
        </Alert>
      );
    }

    // Estado inicial: Mostrar pasos
    return (
      <>
        <p className="text-muted mb-4">
          Actualizá de forma masiva el monto que se les cobrará a tus suscriptores en el próximo período.
        </p>

        <div className="mb-4">
          <h5 className="mb-3 text-muted font-weight-normal">
            1. Descargá la plantilla
          </h5>
          
          <div className="rounded p-3" style={{ border: '1px solid #dee2e6' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0 recurring-payments-text-wrap">{plan?.name || 'Plan sin nombre'}</h6>
              <span className="text-muted">
                <i className="mdi mdi-account-multiple mr-1"></i>
                {plan?.totalSubscriptions || 0} {plan?.totalSubscriptions === 1 ? 'suscriptor' : 'suscriptores'}
              </span>
            </div>
            
            {/* Línea divisoria */}
            <hr className="my-3" />
            
            {/* Segunda fila: Descripción y botón */}
            <div className="d-flex justify-content-between align-items-center">
              <div className="flex-grow-1 mr-3">
                <small className="text-muted">
                  Descargá la plantilla con la información de tus suscriptores.<br />
                  Solo tenés que completar la columna <strong>"Monto actual"</strong> y cargar el archivo. También podés agregar un identificador interno para la suscripción (opcional).
                </small>
              </div>
              <Button
                color="primary"
                outline
                onClick={handleDownloadTemplate}
                disabled={isDownloadingTemplate}
                style={{ whiteSpace: 'nowrap' }}
              >
                {isDownloadingTemplate ? (
                  <>
                    <i className="mdi mdi-loading mdi-spin mr-1" />
                    {__('dashboard.sections.recurringPayments.plans.bulkUpdate.downloading')}
                  </>
                ) : (
                  <>
                    <i className="mdi mdi-download mr-1" />
                    {__('dashboard.sections.recurringPayments.plans.bulkUpdate.downloadTemplate')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <h5 className="mb-3 text-muted font-weight-normal">
            2. Cargá la plantilla con los montos ingresados
          </h5>
          
          <Dropzone
            onDrop={onDrop}
            accept=".csv"
            multiple={false}
            disabled={isValidating}
          >
            {({ getRootProps, getInputProps, isDragActive }) => (
              <div
                {...getRootProps()}
                className="rounded text-center"
                style={{
                  border: `2px dashed ${file ? '#28a745' : (isDragActive ? '#007bff' : '#ccc')}`,
                  padding: '40px 20px',
                  cursor: isValidating ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  backgroundColor: file ? '#f0fff4' : (isDragActive ? '#f0f7ff' : '#fafafa')
                }}
              >
                <input {...getInputProps()} disabled={isValidating} />
                <i className="mdi mdi-cloud-upload mdi-48px text-muted mb-3 d-block"></i>
                {file ? (
                  <div>
                    <p className="text-success font-weight-bold mb-2">
                      <i className="mdi mdi-file-check mr-1"></i>
                      {file.name}
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="font-weight-bold mb-1">
                      Arrastra tu archivo CSV aquí
                    </p>
                    <small className="text-muted">
                      o haz click para seleccionar
                    </small>
                  </>
                )}
              </div>
            )}
          </Dropzone>

          <small className="text-muted d-block mt-2">
            <i className="mdi mdi-information-outline mr-1"></i>
            El archivo será validado automáticamente al cargarlo
          </small>
        </div>
      </>
    );
  };

  return (
    <DashboardModal
      name="updateAmountsModal"
      headerText="Actualizar montos"
      showModal={showModal}
      hideModal={hideModal}
      showFooter={false}
      hideSeparator={true}
      size="lg"
    >
      {renderContent()}
    </DashboardModal>
  );
};

